/**
 * WebSocket Client Service
 * 
 * Handles real-time communication with the backend WebSocket server
 * for cross-device synchronization events.
 */

import { getDeviceManager } from './device-manager';

export interface SyncEvent {
  type: 'message.created' | 'message.updated' | 'conversation.updated' | 'conversation.deleted' | 'read_state.updated';
  conversationId: string;
  messageId?: string;
  data: any;
  timestamp: Date;
  sequenceNumber: number;
  userId: string;
  deviceId: string;
}

export interface WebSocketMessage {
  type: string;
  data?: any;
  event?: SyncEvent;
  conversationId?: string;
  messageId?: string;
  timestamp?: string;
}

export interface ConnectionStatus {
  connected: boolean;
  connecting: boolean;
  lastConnected?: Date;
  reconnectAttempts: number;
  error?: string;
}

class WebSocketClient {
  private ws: WebSocket | null = null;
  private connectionStatus: ConnectionStatus = {
    connected: false,
    connecting: false,
    reconnectAttempts: 0
  };
  private reconnectTimer: NodeJS.Timeout | null = null;
  private pingTimer: NodeJS.Timeout | null = null;
  private maxReconnectAttempts = 10;
  private reconnectDelay = 1000; // Start with 1 second
  private maxReconnectDelay = 30000; // Max 30 seconds
  
  // Event listeners
  private eventListeners: Map<string, Array<(event: SyncEvent) => void>> = new Map();
  private statusListeners: Array<(status: ConnectionStatus) => void> = [];
  
  // Authentication
  private authToken: string | null = null;
  private baseUrl: string;

  constructor() {
    // Determine WebSocket URL - use backend API URL
    const apiUrl = import.meta.env.VITE_API_URL || 
      (import.meta.env.DEV ? 'http://localhost:9876' : 'https://main-veritus-api-production.up.railway.app');
    
    // Convert HTTP(S) URL to WebSocket URL
    this.baseUrl = apiUrl.replace(/^https?:/, window.location.protocol === 'https:' ? 'wss:' : 'ws:');
  }

  /**
   * Connect to WebSocket server
   */
  async connect(token: string): Promise<void> {
    if (this.connectionStatus.connecting || this.connectionStatus.connected) {
      return;
    }

    this.authToken = token;
    this.connectionStatus.connecting = true;
    this.connectionStatus.error = undefined;
    this.notifyStatusChange();

    try {
      const deviceManager = getDeviceManager();
      const deviceInfo = deviceManager.getDeviceInfo();
      
      const wsUrl = new URL('/ws', this.baseUrl);
      wsUrl.searchParams.set('token', token);
      wsUrl.searchParams.set('deviceId', deviceInfo.deviceId);
      wsUrl.searchParams.set('deviceType', deviceInfo.deviceType);

      console.log('🔌 Connecting to WebSocket:', wsUrl.toString().replace(token, '[TOKEN]'));

      this.ws = new WebSocket(wsUrl.toString());
      this.setupEventHandlers();

    } catch (error) {
      console.error('❌ WebSocket connection failed:', error);
      this.connectionStatus.connecting = false;
      this.connectionStatus.error = error instanceof Error ? error.message : 'Connection failed';
      this.notifyStatusChange();
      this.scheduleReconnect();
    }
  }

  /**
   * Setup WebSocket event handlers
   */
  private setupEventHandlers(): void {
    if (!this.ws) return;

    this.ws.onopen = () => {
      console.log('✅ WebSocket connected');
      this.connectionStatus.connected = true;
      this.connectionStatus.connecting = false;
      this.connectionStatus.lastConnected = new Date();
      this.connectionStatus.reconnectAttempts = 0;
      this.connectionStatus.error = undefined;
      this.notifyStatusChange();

      // Start ping timer
      this.startPingTimer();
    };

    this.ws.onmessage = (event) => {
      try {
        const message: WebSocketMessage = JSON.parse(event.data);
        this.handleMessage(message);
      } catch (error) {
        console.error('❌ Failed to parse WebSocket message:', error);
      }
    };

    this.ws.onclose = (event) => {
      console.log('🔌 WebSocket disconnected:', event.code, event.reason);
      this.connectionStatus.connected = false;
      this.connectionStatus.connecting = false;
      
      if (event.code !== 1000) { // Not a normal closure
        this.connectionStatus.error = `Connection closed: ${event.reason || 'Unknown reason'}`;
      }
      
      this.notifyStatusChange();
      this.stopPingTimer();

      // Attempt to reconnect unless it was a normal closure
      if (event.code !== 1000 && this.authToken) {
        this.scheduleReconnect();
      }
    };

    this.ws.onerror = (error) => {
      console.error('❌ WebSocket error:', error);
      this.connectionStatus.error = 'Connection error';
      this.notifyStatusChange();
    };
  }

  /**
   * Handle incoming WebSocket messages
   */
  private handleMessage(message: WebSocketMessage): void {
    switch (message.type) {
      case 'connected':
        console.log('🔌 WebSocket connection confirmed:', message.data);
        break;

      case 'pong':
        // Ping response received
        break;

      case 'sync_event':
        if (message.event) {
          this.handleSyncEvent(message.event);
        }
        break;

      case 'error':
        console.error('❌ WebSocket server error:', message.data);
        break;

      default:
        console.log('📨 Unknown WebSocket message type:', message.type);
    }
  }

  /**
   * Handle sync events from other devices
   */
  private handleSyncEvent(event: SyncEvent): void {
    console.log('⚡ Received sync event:', event.type, event.conversationId);
    
    // Convert timestamp string to Date if needed
    if (typeof event.timestamp === 'string') {
      event.timestamp = new Date(event.timestamp);
    }

    // Notify listeners for this event type
    const listeners = this.eventListeners.get(event.type) || [];
    listeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('❌ Error in sync event listener:', error);
      }
    });

    // Notify listeners for all events
    const allListeners = this.eventListeners.get('*') || [];
    allListeners.forEach(listener => {
      try {
        listener(event);
      } catch (error) {
        console.error('❌ Error in sync event listener:', error);
      }
    });
  }

  /**
   * Send message to WebSocket server
   */
  private send(message: WebSocketMessage): void {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify(message));
    } else {
      console.warn('⚠️ WebSocket not connected, cannot send message');
    }
  }

  /**
   * Subscribe to conversation updates
   */
  subscribeToConversation(conversationId: string): void {
    this.send({
      type: 'subscribe',
      conversationId
    });
  }

  /**
   * Unsubscribe from conversation updates
   */
  unsubscribeFromConversation(conversationId: string): void {
    this.send({
      type: 'unsubscribe',
      conversationId
    });
  }

  /**
   * Request sync updates
   */
  requestSync(sinceSequence: number = 0): void {
    this.send({
      type: 'sync_request',
      data: { sinceSequence }
    });
  }

  /**
   * Add event listener for sync events
   */
  addEventListener(eventType: string, listener: (event: SyncEvent) => void): () => void {
    if (!this.eventListeners.has(eventType)) {
      this.eventListeners.set(eventType, []);
    }
    
    this.eventListeners.get(eventType)!.push(listener);

    // Return unsubscribe function
    return () => {
      const listeners = this.eventListeners.get(eventType);
      if (listeners) {
        const index = listeners.indexOf(listener);
        if (index > -1) {
          listeners.splice(index, 1);
        }
      }
    };
  }

  /**
   * Add status change listener
   */
  onStatusChange(listener: (status: ConnectionStatus) => void): () => void {
    this.statusListeners.push(listener);
    
    // Return unsubscribe function
    return () => {
      const index = this.statusListeners.indexOf(listener);
      if (index > -1) {
        this.statusListeners.splice(index, 1);
      }
    };
  }

  /**
   * Notify status change listeners
   */
  private notifyStatusChange(): void {
    this.statusListeners.forEach(listener => {
      try {
        listener(this.getConnectionStatus());
      } catch (error) {
        console.error('❌ Error in status change listener:', error);
      }
    });
  }

  /**
   * Get current connection status
   */
  getConnectionStatus(): ConnectionStatus {
    return { ...this.connectionStatus };
  }

  /**
   * Schedule reconnection attempt
   */
  private scheduleReconnect(): void {
    if (this.connectionStatus.reconnectAttempts >= this.maxReconnectAttempts) {
      console.error('❌ Max reconnection attempts reached');
      this.connectionStatus.error = 'Max reconnection attempts reached';
      this.notifyStatusChange();
      return;
    }

    const delay = Math.min(
      this.reconnectDelay * Math.pow(2, this.connectionStatus.reconnectAttempts),
      this.maxReconnectDelay
    );

    console.log(`🔄 Scheduling reconnect in ${delay}ms (attempt ${this.connectionStatus.reconnectAttempts + 1})`);

    this.reconnectTimer = setTimeout(() => {
      this.connectionStatus.reconnectAttempts++;
      if (this.authToken) {
        this.connect(this.authToken);
      }
    }, delay);
  }

  /**
   * Start ping timer to keep connection alive
   */
  private startPingTimer(): void {
    this.pingTimer = setInterval(() => {
      this.send({ type: 'ping' });
    }, 30000); // Ping every 30 seconds
  }

  /**
   * Stop ping timer
   */
  private stopPingTimer(): void {
    if (this.pingTimer) {
      clearInterval(this.pingTimer);
      this.pingTimer = null;
    }
  }

  /**
   * Disconnect WebSocket
   */
  disconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }

    this.stopPingTimer();

    if (this.ws) {
      this.ws.close(1000, 'Client disconnect');
      this.ws = null;
    }

    this.connectionStatus.connected = false;
    this.connectionStatus.connecting = false;
    this.authToken = null;
    this.notifyStatusChange();
  }

  /**
   * Check if connected
   */
  isConnected(): boolean {
    return this.connectionStatus.connected;
  }
}

// Singleton instance
let wsClientInstance: WebSocketClient | null = null;

export function getWebSocketClient(): WebSocketClient {
  if (!wsClientInstance) {
    wsClientInstance = new WebSocketClient();
  }
  return wsClientInstance;
}

export { WebSocketClient };